import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  CommonModule,
  DOCUMENT,
  isPlatformBrowser,
  NgClass,
  NgIf
} from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule
} from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { environment } from '@Environments';
import { NavigationService } from '@Shared/services';
import { ScreenSizeService } from '@Shared/services';
import { NotificationService, StorageService } from '@Shared/services';
import { UserCommonApiService } from '@App/common/services';
import { CardPackageService } from '@App/modules/cards-packages/services';
import { PageTitleService } from '@App/common/services/title.service';
import { GoogleAnalyticsService } from '@App/common/services/google-analytics.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslateService } from '@ngx-translate/core';
import {
  CountryInputComponent,
  FooterComponent,
  LoaderComponent,
  VerifyFormComponent,
  WelcomePageHeaderComponent
} from '@Shared/components';
import { AuthComponent } from './pages/auth/auth.component';
import { ToastModule } from 'primeng/toast';
import { ProFeatureModalComponent } from '@App/shared/components/pro-feature-modal/pro-feature-modal.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { AppSettings } from '@Shared/app.settings';
import { OtpRequestModel } from '@App/common/models';
import { VerificationResourceEnum } from '@App/common/enums';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    WelcomePageHeaderComponent,
    CountryInputComponent,
    VerifyFormComponent,
    LoaderComponent,
    ToastModule,
    AuthComponent,
    FooterComponent,
    ConfirmDialogModule,
    DialogModule,
    ButtonModule,
    ProFeatureModalComponent,
    FontAwesomeModule,
    NgClass,
    NgIf
  ]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'app';
  signedIn = false;
  $hideLinks: Subscription;
  $showHeader: Subscription;
  isShowHeader = true;
  destroyRef = inject(DestroyRef);
  isBrowser: boolean;

  @ViewChild('headerBar') headerBar: ElementRef;
  isMobile: boolean;
  isSharePage: boolean;
  phoneRequiredDialogVisible = false;
  phoneValidationError = '';
  phoneOtpErrorMessage = '';
  phoneDialogStep: 'phone' | 'otp' = 'phone';
  phoneOtpResourceType: VerificationResourceEnum =
    VerificationResourceEnum.Phone;
  phoneLogoutInProgress = false;
  phoneRequiredForm = new FormGroup({
    contactPhone: new FormControl(null, Validators.required)
  });
  otpRequestModel = new OtpRequestModel();
  private requiredPhoneUserId: string | null = null;
  private requiredPhoneProfile: any = null;
  private phoneRequirementCheckInProgress = false;

  constructor(
    private screenSizeService: ScreenSizeService,
    private cardPackageService: CardPackageService,
    public navigationService: NavigationService,
    private pageTitleService: PageTitleService,
    @Inject(PLATFORM_ID) private platformId: any,
    private userCommonService: UserCommonApiService,
    private googleAnalyticsService: GoogleAnalyticsService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    this.pageTitleService.init();
    this.pageTitleService.setTitle(environment.title);
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Set isSharePage HERE in the constructor so every binding that depends
    // on it ([hidden], [ngClass], *ngIf) has the correct value before Angular's
    // very first render/hydration check.  Setting it only in ngOnInit() leaves
    // it undefined during the initial render, causing a hydration mismatch
    // (server rendered with isSharePage=true, client first renders with
    // isSharePage=undefined) → Angular re-renders → footer/header flash white.
    //
    // We still keep a pathname fallback here because ActivatedRoute snapshot is
    // not yet populated in the constructor; the route-data-driven check takes
    // over from ngOnInit() / NavigationEnd onwards.
    const { pathname } = this.document.location;
    this.isSharePage =
      pathname.includes('/card/') ||
      pathname.includes('/embedded-card/');

    // Initialize translation service
    this.translateService.setFallbackLang('en');
    this.translateService.use('en');
    this.isMobile = this.screenSizeService.isMobile;
    if (this.isBrowser) {
      if (environment.trackPageviews) {
        this.addHotJar();
      }
    }
    this.navigationService.startSaveHistory();
  }

  get businessType() {
    return this.cardPackageService?.selectedType;
  }

  ngOnInit() {
    // Keep isSharePage in sync on every completed SPA navigation using route
    // data so that any route with `data: { hideAppShell: true }` suppresses
    // the header/footer, rather than relying on URL string matching.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isSharePage = this.routeHidesShell();
      });

    this.$hideLinks = this.userCommonService.hideLinks.subscribe(
      (isSignedIn) => {
        this.signedIn = isSignedIn;
        if (isSignedIn) {
          this.evaluatePhoneRequirement(true);
        } else {
          this.closePhoneRequirementDialog();
        }
      }
    );

    this.$showHeader = this.userCommonService.showHeader.subscribe((show) => {
      this.isShowHeader = show;
    });
    // Google Analytics/meta robots logic from BootstrapComponent
    if (environment?.title === 'Pitch59' && environment?.production) {
      this.googleAnalyticsService.initialize();
    } else {
      const metaTag = this.renderer.createElement('meta');
      this.renderer.setAttribute(metaTag, 'name', 'robots');
      this.renderer.setAttribute(metaTag, 'content', 'noindex, nofollow');
      this.renderer.appendChild(this.document.head, metaTag);
    }
    this.setRealHeight();

    if (this.userCommonService.isAuthenticated()) {
      this.evaluatePhoneRequirement();
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.setRealHeight();
    }
  }

  ngAfterViewInit(): void {
    // this.initSmartBanner();
  }

  ngOnDestroy() {
    if (this.$hideLinks) {
      this.$hideLinks.unsubscribe();
    }

    if (this.$showHeader) {
      this.$showHeader.unsubscribe();
    }
    this.pageTitleService.ngOnDestroy();
  }

  /** Walk the active route tree and return true when any route carries `data.hideAppShell`. */
  private routeHidesShell(): boolean {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data?.['hideAppShell'] === true;
  }

  addHotJar() {
    const script = this.document.createElement('script');
    script.setAttribute('type', 'text/javascript');
    script.innerText =
      "(function(h,o,t,j,a,r){ h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:2607843,hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script');r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv; a.appendChild(r); })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');";
    this.document.head.appendChild(script);
  }

  private setRealHeight() {
    if (!this.isBrowser) return;
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      this.document.documentElement.style.setProperty(
        '--real-vh',
        `${window.innerHeight * 0.01}px`
      );
    }
  }

  saveRequiredPhoneNumber(): void {
    const contactPhone = this.phoneRequiredForm.controls.contactPhone
      .value as any;
    const normalizedNationalNumber = String(
      contactPhone?.nationalNumber || ''
    ).trim();

    if (!this.requiredPhoneUserId) {
      return;
    }

    if (!normalizedNationalNumber) {
      this.phoneValidationError = 'Phone number is required';
      this.phoneRequiredForm.controls.contactPhone.markAsTouched();
      return;
    }

    this.fillPhoneOtpRequestModel();
    this.phoneValidationError = '';
    this.phoneOtpErrorMessage = '';
    this.userCommonService
      .sendOtpForContactNumberChange(this.otpRequestModel)
      .subscribe({
        next: ({ message }) => {
          this.notificationService.showNotification(
            'SUCCESS',
            message || 'Verification code sent'
          );
          this.phoneDialogStep = 'otp';
        },
        error: (error) => {
          this.notificationService.showNotification(
            'ERROR',
            error?.message || 'Failed to send verification code'
          );
        }
      });
  }

  verifyRequiredPhoneOtp(event: { otpCode: number }): void {
    if (!event?.otpCode) {
      return;
    }

    const contactPhone = this.phoneRequiredForm.controls.contactPhone
      .value as any;
    const normalizedNationalNumber = String(
      contactPhone?.nationalNumber || ''
    ).trim();
    const countryIsoCode = contactPhone?.countryIsoCode || 'US';

    if (!this.requiredPhoneUserId || !normalizedNationalNumber) {
      return;
    }

    this.fillPhoneOtpRequestModel();
    this.otpRequestModel.smsOtpCode = event.otpCode;
    this.phoneValidationError = '';
    this.phoneOtpErrorMessage = '';

    this.userCommonService.verifyEmailSmsOTP(this.otpRequestModel).subscribe({
      next: () => {
        this.userCommonService
          .updateUserProfile({
            id: this.requiredPhoneUserId,
            phone: {
              countryIsoCode,
              nationalNumber: normalizedNationalNumber
            },
            smsOtpCode: event.otpCode
          })
          .subscribe({
            next: () => {
              this.notificationService.showNotification(
                'SUCCESS',
                'Phone number added successfully'
              );
              this.evaluatePhoneRequirement(true);
            },
            error: (error) => {
              this.notificationService.showNotification(
                'ERROR',
                error?.message || 'Failed to save phone number'
              );
            }
          });
      },
      error: (error) => {
        this.phoneOtpErrorMessage =
          error?.message || 'Incorrect verification code';
      }
    });
  }

  resendRequiredPhoneOtp(): void {
    this.fillPhoneOtpRequestModel();
    this.phoneOtpErrorMessage = '';
    this.userCommonService
      .sendOtpForContactNumberChange(this.otpRequestModel)
      .subscribe({
        next: () => {
          this.notificationService.showNotification(
            'SUCCESS',
            'Verification code resent'
          );
        },
        error: (error) => {
          this.notificationService.showNotification(
            'ERROR',
            error?.message || 'Failed to resend verification code'
          );
        }
      });
  }

  logoutFromRequiredPhoneDialog(): void {
    if (this.phoneLogoutInProgress) {
      return;
    }

    this.phoneLogoutInProgress = true;
    this.userCommonService.logout().subscribe({
      next: () => this.finishRequiredPhoneLogout(),
      error: () => this.finishRequiredPhoneLogout()
    });
  }

  private finishRequiredPhoneLogout(): void {
    this.phoneLogoutInProgress = false;
    this.userCommonService.isSignIn.next(false);
    this.userCommonService.clearSession();
    this.closePhoneRequirementDialog();
    this.router.navigate(['']);
  }

  private evaluatePhoneRequirement(force = false): void {
    if (!this.isBrowser) {
      return;
    }

    if (this.phoneRequirementCheckInProgress && !force) {
      return;
    }

    const userDetailsRaw = this.storageService.getItem(
      AppSettings.USER_DETAILS
    );
    const token = this.storageService.getItemFromCookies(AppSettings.TOKEN_KEY);
    let userDetails: any = userDetailsRaw;

    if (typeof userDetailsRaw === 'string') {
      try {
        userDetails = JSON.parse(userDetailsRaw);
      } catch (error) {
        userDetails = null;
      }
    }

    const userId = userDetails?.userId || userDetails?.id;

    if (!token || !userId) {
      this.closePhoneRequirementDialog();
      return;
    }

    this.phoneRequirementCheckInProgress = true;
    this.userCommonService.getUserProfile(userId).subscribe({
      next: ({ data }) => {
        const hasContactNumber = !!String(data?.contactNumber || '').trim();
        const hasPhoneObjectNumber = !!String(
          data?.phone?.nationalNumber || ''
        ).trim();
        const hasPhoneNumber = hasContactNumber || hasPhoneObjectNumber;

        this.requiredPhoneUserId = data?.id || userId;
        this.requiredPhoneProfile = data || null;

        if (hasPhoneNumber) {
          this.closePhoneRequirementDialog();
        } else {
          this.phoneRequiredForm.patchValue(
            {
              contactPhone: data?.phone || null
            },
            { emitEvent: false }
          );
          this.phoneDialogStep = 'phone';
          this.phoneOtpErrorMessage = '';
          this.phoneValidationError = '';
          this.phoneRequiredDialogVisible = true;
        }
        this.phoneRequirementCheckInProgress = false;
      },
      error: () => {
        this.phoneRequirementCheckInProgress = false;
      }
    });
  }

  private closePhoneRequirementDialog(): void {
    this.phoneRequiredDialogVisible = false;
    this.phoneValidationError = '';
    this.phoneOtpErrorMessage = '';
    this.phoneDialogStep = 'phone';
    this.requiredPhoneUserId = null;
    this.requiredPhoneProfile = null;
  }

  private fillPhoneOtpRequestModel(): void {
    const contactPhone = this.phoneRequiredForm.controls.contactPhone
      .value as any;
    this.otpRequestModel.firstName = this.requiredPhoneProfile?.firstName || '';
    this.otpRequestModel.lastName = this.requiredPhoneProfile?.lastName || '';
    this.otpRequestModel.emailId = this.requiredPhoneProfile?.emailId || '';
    this.otpRequestModel.contactNumber =
      this.requiredPhoneProfile?.contactNumber || '';
    this.otpRequestModel.phone = {
      countryIsoCode: contactPhone?.countryIsoCode || 'US',
      nationalNumber: String(contactPhone?.nationalNumber || '').trim()
    };
    this.otpRequestModel.newUser = '';
    this.otpRequestModel.type = VerificationResourceEnum.Phone;
    this.otpRequestModel.smsOtpCode = null;
  }
}
